import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./ClubLandingPage.css";

/**
 * ClubLandingPage — parameterised React port of the sportx_open_{club}.html
 * mockups. Rendered at:
 *   /formby              → CLUB_CONFIGS.formby
 *   /hesketh             → CLUB_CONFIGS.hesketh
 *   /southport-ainsdale  → CLUB_CONFIGS['southport-ainsdale']
 *
 * The three pages share 100% of markup + copy structure — only accent
 * colour, name strings, and crest image change. That's driven by the
 * `club` prop.
 *
 * CTA behaviour: clicking any primary CTA stashes the club slug in
 * localStorage (`sportx_club_slug`) and navigates to /create-competition.
 * When the user eventually signs up, the backend user-model wire-up
 * (Phase B) will read that slug and persist it as `user.club` for later
 * grouping / analytics. Cross-club visibility is intentionally allowed.
 */

// Real Open Championship first-tee time (Royal Birkdale, Thu 16 Jul 2026).
const KICKOFF = new Date("2026-07-16T05:30:00Z");

// Auction widget event script — same 19-second loop the designer used.
const WIDGET_EVENTS = [
  { at: 5000, bid: "£20m", leader: "Tom outbid James", leaderColor: "var(--gold)" },
  { at: 11000, bid: "£30m", leader: "James leading", leaderColor: "var(--teal)" },
  { at: 16000, bid: "£35m", leader: "James leading", leaderColor: "var(--teal)" },
];

// Scorecard "top of leaderboard" flip cycle.
const SCORECARD_CYCLE = [
  { s: "-8", p: "132" },
  { s: "-9", p: "131" },
  { s: "-8", p: "132" },
];

export const CLUB_CONFIGS = {
  formby: {
    slug: "formby",
    fullName: "Formby Golf Club",
    displayName: "Formby Golf Club",
    shortName: "Formby",
    accentColor: "#0a5c33",
    accentDim: "rgba(10,92,51,0.18)",
    crest: "/club-crests/formby.svg",
  },
  hesketh: {
    slug: "hesketh",
    fullName: "Hesketh Golf Club",
    displayName: "Hesketh Golf Club",
    shortName: "Hesketh",
    accentColor: "#8b1e2f",
    accentDim: "rgba(139,30,47,0.2)",
    crest: "/club-crests/hesketh.png",
    // Hesketh PNG has baked-in whitespace, so "contain" makes the visible
    // mark look tiny in the pinhead. A modest zoom fills the circle without
    // clipping the "HESKETH" wordmark under the shield.
    crestScale: "125%",
    crestPadding: 6,
  },
  "southport-ainsdale": {
    slug: "southport-ainsdale",
    fullName: "Southport & Ainsdale Golf Club",
    displayName: "Southport & Ainsdale",
    shortName: "S\u0026A",
    accentColor: "#1a2e6b",
    accentDim: "rgba(26,46,107,0.22)",
    crest: "/club-crests/southport-ainsdale.svg",
  },
};

// ---- Countdown hook -----------------------------------------------------

function useKickoffCountdown() {
  const compute = () => {
    const diff = KICKOFF.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hrs: 0, mins: 0, secs: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hrs: Math.floor((diff % 86400000) / 3600000),
      mins: Math.floor((diff % 3600000) / 60000),
      secs: Math.floor((diff % 60000) / 1000),
    };
  };
  const [t, setT] = useState(compute);
  useEffect(() => {
    const i = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(i);
  }, []);
  return t;
}

const pad = (n) => String(n).padStart(2, "0");

// ---- Live auction widget (McIlroy demo loop) ----------------------------

function LiveAuctionWidget() {
  const DURATION = 19;
  const [remaining, setRemaining] = useState(DURATION);
  const [bid, setBid] = useState("£15m");
  const [leader, setLeader] = useState("James leading");
  const [leaderColor, setLeaderColor] = useState("var(--muted)");
  const [showToast, setShowToast] = useState(false);
  const startRef = useRef(null);
  const firedRef = useRef(new Set());
  const rafRef = useRef(null);

  useEffect(() => {
    const loop = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const rem = Math.max(0, DURATION - Math.floor(elapsed / 1000));
      setRemaining(rem);

      for (const ev of WIDGET_EVENTS) {
        if (!firedRef.current.has(ev.at) && elapsed >= ev.at) {
          firedRef.current.add(ev.at);
          setBid(ev.bid);
          setLeader(ev.leader);
          setLeaderColor(ev.leaderColor);
        }
      }

      if (elapsed < DURATION * 1000) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2200);
        // Restart the loop after a 3s pause.
        setTimeout(() => {
          startRef.current = null;
          firedRef.current.clear();
          setBid("£15m");
          setLeader("James leading");
          setLeaderColor("var(--muted)");
          setRemaining(DURATION);
          rafRef.current = requestAnimationFrame(loop);
        }, 3000);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const pct = Math.max(0, (remaining / DURATION) * 100);

  return (
    <div className="widget-wrap" data-testid="live-auction-widget">
      <div className="widget-top">
        <div className="w-timer">00:{pad(remaining)}</div>
        <div className="live-badge"><div className="live-dot" /> LIVE</div>
      </div>
      <div className="w-flag">🇮🇪</div>
      <div className="w-name">Rory McIlroy</div>
      <div className="w-meta">Ireland · World No. 3</div>
      <div className="w-bid-label">Current highest bid</div>
      <div className="w-bid">{bid}</div>
      <div className="w-leader" style={{ color: leaderColor }}>{leader}</div>
      <div className="w-bar"><div className="w-bar-fill" style={{ width: `${pct}%` }} /></div>
      <div className="w-btns">
        <div className="w-btn">+£1m</div>
        <div className="w-btn">+£5m</div>
        <div className="w-btn">+£10m</div>
        <div className="w-btn">+£20m</div>
        <div className="w-btn">+£50m</div>
        <div className="w-btn pass">Pass</div>
      </div>
      <div className="w-players-row">
        <div className="w-avs">
          <div className="w-av" style={{ background: "#1ec8e0" }}>JA</div>
          <div className="w-av" style={{ background: "#c9a84c" }}>TO</div>
          <div className="w-av" style={{ background: "#a8bdd0" }}>NI</div>
          <div className="w-av" style={{ background: "#7a9ab8", color: "white" }}>CH</div>
        </div>
        <span className="w-av-text">4 in the group</span>
      </div>
      <div className="w-caption">Live auction · 30 seconds per player</div>
      <div className={`w-toast${showToast ? " show" : ""}`}>🏆 McIlroy — won by James</div>
    </div>
  );
}

// ---- Animated top-of-leaderboard scorecard ------------------------------

function AnimatedScorecard() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setIdx((v) => (v + 1) % SCORECARD_CYCLE.length), 4000);
    return () => clearInterval(i);
  }, []);
  const top = SCORECARD_CYCLE[idx];
  return (
    <div className="scorecard-wrap" data-testid="animated-scorecard">
      <div className="sc-header">
        <span className="sc-title">Your leaderboard</span>
        <span className="sc-round">Thru 36</span>
        <div className="sc-live"><div className="sc-live-dot" /> LIVE</div>
      </div>
      <div className="sc-progress">
        <span className="sc-progress-text">36 / 72</span>
        <div className="sc-progress-bar"><div className="sc-progress-fill" style={{ width: "50%" }} /></div>
      </div>
      <div className="sc-cols">
        <div className="sc-col-label" />
        <div className="sc-col-label">Player</div>
        <div className="sc-col-label right">Score</div>
        <div className="sc-col-label right">Pts</div>
      </div>
      <div className="sc-row highlight">
        <div className="sc-pos gold">1</div>
        <div>
          <div className="sc-player-name">McIlroy <span className="sc-player-flag">🇮🇪</span></div>
          <div className="sc-owner">James</div>
        </div>
        <div className="sc-score under">{top.s}</div>
        <div className="sc-pts leading">{top.p}</div>
      </div>
      <div className="sc-row">
        <div className="sc-pos">2</div>
        <div>
          <div className="sc-player-name">Scheffler <span className="sc-player-flag">🇺🇸</span></div>
          <div className="sc-owner">Tom</div>
        </div>
        <div className="sc-score under">-6</div>
        <div className="sc-pts">134</div>
      </div>
      <div className="sc-row">
        <div className="sc-pos">3</div>
        <div>
          <div className="sc-player-name">Fitzpatrick <span className="sc-player-flag">🏴󠁧󠁢󠁥󠁮󠁧󠁿</span></div>
          <div className="sc-owner">Nick</div>
        </div>
        <div className="sc-score under">-3</div>
        <div className="sc-pts">137</div>
      </div>
      <div className="sc-row">
        <div className="sc-pos">4</div>
        <div>
          <div className="sc-player-name">Fleetwood <span className="sc-player-flag">🏴󠁧󠁢󠁥󠁮󠁧󠁿</span></div>
          <div className="sc-owner">Charlie</div>
        </div>
        <div className="sc-score over">+1</div>
        <div className="sc-pts">141</div>
      </div>
    </div>
  );
}

// ---- Main page ----------------------------------------------------------

export default function ClubLandingPage({ club }) {
  const navigate = useNavigate();
  const cd = useKickoffCountdown();

  // Load Barlow / Barlow Condensed once per mount.
  useEffect(() => {
    const linkId = "club-landing-fonts";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Barlow:wght@300;400;500;600&display=swap";
      document.head.appendChild(link);
    }
    // Also set the document title so the tab reads "Formby · The Open · Sport X".
    document.title = `The Open Comp · ${club.fullName} · Sport X`;
  }, [club]);

  // Tag this browser session as "arrived via a golf club landing page" so
  // that Create-Competition, sport filters, and nav all show golf-only.
  const applyGolfSportMode = () => {
    try {
      localStorage.setItem("sportx_club_slug", club.slug);
      localStorage.setItem("sportMode", "golf");
      sessionStorage.setItem("sportModeSet", "true");
    } catch {
      // localStorage disabled (private-window Safari) — silently continue.
    }
  };

  const handleStart = (e) => {
    e.preventDefault();
    // All three landing-page CTAs (top-right, middle, bottom) go to the app
    // home page in Golf-only mode. The user then clicks Create Comp from
    // there and lands on /create-competition with The Open pre-selected.
    handleToApp(e);
  };

  const handleToApp = (e) => {
    e.preventDefault();
    applyGolfSportMode();
    // Full navigation (not react-router) so SportModeProvider re-initialises
    // and picks up the just-set sessionStorage flag on the next render.
    window.location.href = "/";
  };

  return (
    <div className="club-page-outer" data-testid={`club-page-${club.slug}`}>
      <div className="club-phone-frame">
        {/* Faux hardware chrome (hidden on real mobile via media query) */}
        <div className="club-phone-btn-left" aria-hidden="true" />
        <div className="club-phone-btn-right" aria-hidden="true" />
        <div className="club-phone-notch" aria-hidden="true">
          <div className="club-notch-pill">
            <span className="club-notch-speaker" />
            <span className="club-notch-camera" />
          </div>
        </div>

        <div
          className="club-landing-root"
          data-testid={`club-landing-${club.slug}`}
          style={{ "--club": club.accentColor, "--club-dim": club.accentDim }}
        >
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <div
            aria-label={club.fullName}
            role="img"
            style={{
              width: "34px",
              height: "34px",
              minWidth: "34px",
              minHeight: "34px",
              maxWidth: "34px",
              maxHeight: "34px",
              flex: "0 0 34px",
              borderRadius: "50%",
              backgroundColor: "#ffffff",
              backgroundImage: `url(${club.crest})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: club.crestScale || "contain",
              backgroundOrigin: "content-box",
              padding: `${club.crestPadding ?? 4}px`,
              boxShadow: `0 0 0 2px ${club.accentColor}, 0 2px 6px rgba(0,0,0,0.3)`,
              clipPath: "circle(50%)",
              WebkitClipPath: "circle(50%)",
              display: "inline-block",
              boxSizing: "border-box",
            }}
          />
          <div className="header-name">{club.displayName} <span>The Open 2026</span></div>
        </div>
        <a href="/" onClick={handleToApp} className="header-right">
          <div className="sportx-tag">SPORT X</div>
          <span className="header-to-app">To the app &rarr;</span>
        </a>
      </div>

      {/* Hero */}
      <div className="hero">
        <div
          aria-label={club.fullName}
          role="img"
          style={{
            width: "96px",
            height: "96px",
            minWidth: "96px",
            minHeight: "96px",
            maxWidth: "96px",
            maxHeight: "96px",
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            backgroundImage: `url(${club.crest})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: club.crestScale || "contain",
            backgroundOrigin: "content-box",
            padding: `${club.crestPadding ?? 12}px`,
            boxShadow: `0 0 0 3px ${club.accentColor}, 0 8px 22px rgba(0,0,0,0.35)`,
            clipPath: "circle(50%)",
            WebkitClipPath: "circle(50%)",
            margin: "0 auto 14px",
            position: "relative",
            zIndex: 1,
            boxSizing: "border-box",
            display: "block",
          }}
        />
        <div className="hero-eyebrow">
          For {club.shortName} members · Royal Birkdale · 16&ndash;19 July
        </div>
        <h1>BID BEFORE<br />THE <span className="teal">FIRST TEE.</span></h1>
        <p className="hero-sub">
          A Sport X competition for {club.shortName} members. Bid on real
          players in The Open. Compete against your usual golfing group as
          the tournament plays out.
        </p>
        <p className="hero-note">Winner picks the cause. No gambling. No subscription.</p>
        <div className="hero-pills">
          <div className="pill good">Live auctions</div>
          <div className="pill good">Live sport</div>
          <div className="pill good">Your friends</div>
          <div className="pill no">No gambling</div>
          <div className="pill no">No subscription</div>
        </div>
        <button
          type="button"
          className="hero-cta"
          onClick={handleStart}
          data-testid="club-cta-hero"
        >
          Start your competition &rarr;
        </button>
        <p className="hero-cta-sub">Takes 2 minutes</p>
      </div>

      {/* Countdown */}
      <div className="countdown-bar" data-testid="club-countdown">
        <span className="cd-label">First tee at Royal Birkdale</span>
        <div className="cd-units">
          <div className="cd-unit"><span className="cd-num">{pad(cd.days)}</span><span className="cd-sub">Days</span></div>
          <span className="cd-sep">:</span>
          <div className="cd-unit"><span className="cd-num">{pad(cd.hrs)}</span><span className="cd-sub">Hrs</span></div>
          <span className="cd-sep">:</span>
          <div className="cd-unit"><span className="cd-num">{pad(cd.mins)}</span><span className="cd-sub">Mins</span></div>
          <span className="cd-sep">:</span>
          <div className="cd-unit"><span className="cd-num">{pad(cd.secs)}</span><span className="cd-sub">Secs</span></div>
        </div>
        <span className="cd-date">16 July 2026 · 154th Open Championship</span>
      </div>

      <hr />

      {/* How it works */}
      <div className="section">
        <div className="section-label">How it works</div>
        <div className="section-title">Four steps.<br />Then watch the golf.</div>
        <div className="steps">
          <div className="step"><div className="step-num">01</div><div className="step-title">Set up your group</div><div className="step-body">Create a Sport X competition, give it a name, set the budget, invite your friends with one link. Works in any browser &mdash; no app needed.</div></div>
          <div className="step"><div className="step-num">02</div><div className="step-title">Run the auction</div><div className="step-body">Everyone bids on real Open players with a virtual budget. Scheffler, McIlroy, Fleetwood &mdash; 30 seconds per player. Highest bid wins them.</div></div>
          <div className="step"><div className="step-num">03</div><div className="step-title">Follow every round</div><div className="step-body">Your players score points from their real shots. The leaderboard moves with every hole. No tinkering &mdash; just watch the golf.</div></div>
          <div className="step"><div className="step-num">04</div><div className="step-title">Winner picks the cause</div><div className="step-body">Best squad wins. If your group pooled a pot, the winner decides where it goes &mdash; a charity, a club, a cause they care about.</div></div>
        </div>

        <LiveAuctionWidget />
        <AnimatedScorecard />
        <p className="leaderboard-caption">
          Lowest squad score across the four rounds wins. 1 point per stroke,
          2&times; first two rounds for missing the cut plus 10 extra strokes penalty.
        </p>
      </div>

      <hr />

      {/* Scoring */}
      <div className="section">
        <div className="section-label">Scoring</div>
        <div className="section-title">The golf does<br />the work.</div>
        <div className="scoring-grid">
          <div className="scoring-row is-head">
            <div className="scoring-label-head">What happens</div>
            <div className="scoring-label-head right">Points</div>
          </div>
          <div className="scoring-row">
            <div className="scoring-label"><strong>Every shot played</strong><small>1 point per shot, every round</small></div>
            <div className="scoring-value">+1</div>
          </div>
          <div className="scoring-row">
            <div className="scoring-label"><strong>Missed cut</strong><small>2&times; their R1 + R2 total &plus; 10 strokes penalty</small></div>
            <div className="scoring-value penalty">2&times; &plus;10</div>
          </div>
          <div className="scoring-row">
            <div className="scoring-label"><strong>Lowest total wins</strong><small>Same logic as real golf</small></div>
            <div className="scoring-value trophy">🏆</div>
          </div>
        </div>
      </div>

      <hr />

      {/* Cause */}
      <div className="section">
        <div className="section-label">The cause</div>
        <div className="section-title">Sport done<br />the right way.</div>
        <div className="cause-block">
          <p><strong>Free to play.</strong> No entry fees, no subscription. If your group wants to pool a pot, the winner picks where it goes &mdash; a charity, a club, a cause they care about.</p>
          <p>Not pre-selected. Not corporate. The winner decides. Sport, the way it&apos;s supposed to be &mdash; safe, social, cause-led.</p>
        </div>
      </div>

      <hr />

      {/* Final CTA */}
      <div className="cta-section">
        <div className="cta-title">
          Your {club.shortName} group.<br /><em>Your Open.</em>
        </div>
        <p className="cta-sub">
          Set up takes two minutes. The tournament does the rest. No gambling,
          no subscriptions, no tinkering.
        </p>
        <button
          type="button"
          className="btn-primary"
          onClick={handleStart}
          data-testid="club-cta-footer"
        >
          Start your competition &rarr;
        </button>
        <a href="mailto:support@mysportx.org" className="btn-secondary">Get in touch</a>
        <p className="cta-contact">Questions? <a href="mailto:support@mysportx.org">support@mysportx.org</a></p>
      </div>

      {/* Footer */}
      <div className="footer">
        <div className="footer-wordmark">SPORT <span>X</span></div>
        <div className="footer-note">
          Made for {club.shortName} members by Sport X · Free to play · No gambling<br />
          &copy; 2026 Jana
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}
